import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Modal, ModalDialog, Stack, Table, Typography } from '@mui/joy';
import { importsApi } from '../../lib/apiImports';
import type { DataImport, RewindPreview } from '../../types/imports';

const fieldLabel = (name: string) => name.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const recordType = (model: string) => ({ 'crm.contact': 'Contact', 'crm.address': 'Address', 'crm.contactnote': 'Contact note', 'crm.opportunity': 'Opportunity', 'crm.contactsource': 'Contact source', 'jobs.job': 'Job', 'jobs.tag': 'Tag' }[model] || fieldLabel(model.split('.').pop() || model));

const display = (value: unknown) => value == null || value === '' ? 'Blank' : typeof value === 'object' ? JSON.stringify(value) : String(value);

export const RewindImportModal: React.FC<{
  dataImport: DataImport;
  onClose: () => void;
  onReversed: () => void;
}> = ({ dataImport, onClose, onReversed }) => {
  const [preview, setPreview] = useState<RewindPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setPreview(null);
    setError('');
    importsApi.previewRewind(dataImport.id).then((value) => {
      if (active) setPreview(value);
    }).catch((err: Error) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [dataImport.id, revision]);

  const proceed = async () => {
    if (!preview) return;
    setBusy(true);
    setError('');
    try {
      await importsApi.rewindImport(dataImport.id, preview.token);
      onReversed();
      onClose();
    } catch (err) {
      const failure = err as Error & { preview?: RewindPreview };
      setError(failure.message || 'Rewind failed. Refresh the preview before retrying.');
      // A stale approval never executes. Present the fresh differences for
      // another explicit click, rather than silently retrying the deletion.
      setPreview(failure.preview ?? null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => { if (!busy) onClose(); }}>
      <ModalDialog sx={{ width: 900, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
        <Typography level="h3">Rewind import</Typography>
        <Typography>{dataImport.original_filename || `Import #${dataImport.id}`}</Typography>
        <Typography level="body-sm">
          This permanently deletes records added by this import and restores imported changes to existing records.
          Unrelated edits on existing records are kept. Review the affected records before proceeding.
        </Typography>
        {error && <Alert color="danger">{error}</Alert>}
        {!preview && !error && <Typography>Loading reversal preview…</Typography>}
        {preview && <>
          <Alert color={preview.changed_count || preview.related_records.length ? 'warning' : 'neutral'}>
            {preview.delete_count} records to delete · {preview.restore_count} records to restore · {preview.changed_count} records changed since import.
            {preview.missing_count > 0 && ` ${preview.missing_count} records are already missing and will not be recreated.`}
          </Alert>
          {preview.records.map((record) => (
            <Box key={`${record.model}:${record.id}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              <Typography level="title-sm">{record.action === 'delete' ? 'Delete' : record.action === 'restore' ? 'Restore imported changes' : 'Already missing'}: {record.label}</Typography>
              <Typography level="body-xs">{recordType(record.model)} #{record.id}</Typography>
              {record.changes.length > 0 && <Table size="sm" sx={{ '& td': { overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' } }}>
                <thead><tr><th>Field</th><th>Before import</th><th>Imported</th><th>Current</th></tr></thead>
                <tbody>{record.changes.map((change) => <tr key={`${change.section}:${fieldLabel(change.field)}`}>
                  <td>{fieldLabel(change.field)}</td><td>{display(change.before_import)}</td><td>{display(change.imported)}</td><td>{display(change.current)}</td>
                </tr>)}</tbody>
              </Table>}
              {record.later_changes.length > 0 && <Box sx={{ mt: 1 }}>
                <Typography level="title-sm" color="warning">Changed after import</Typography>
                {record.later_changes.map((change) => <Typography key={`${change.section}:${fieldLabel(change.field)}`} level="body-sm" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                  {fieldLabel(change.field)}: {display(change.imported)} → {display(change.current)}
                  {' — '}{record.action === 'delete' ? 'will be deleted' : change.will_overwrite ? (change.section === 'relations' ? 'imported link changes will be reversed' : change.field === 'weighted_value' ? 'will be recalculated' : 'will be restored to before import') : 'will be kept'}
                </Typography>)}
              </Box>}
            </Box>
          ))}
          {preview.related_records.length > 0 && <Box>
            <Typography level="title-md">Related data affected by deletion</Typography>
            <Typography level="body-sm">This may include work added after the import.</Typography>
            {preview.related_records.map((record, index) => <Typography key={`${record.model}:${record.id}:${index}`} level="body-sm">
              {record.action === 'unlink' ? `Clear ${fieldLabel(record.field || 'link')} on` : record.action === 'remove_link' ? 'Remove link:' : 'Delete:'} {record.label} ({recordType(record.model)} #{record.id})
            </Typography>)}
          </Box>}
          {preview.blockers.map((record) => <Alert key={`${record.model}:${record.id}`} color="danger">
            {record.label}: {record.reason}
          </Alert>)}
        </>}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="plain" disabled={busy} onClick={() => setRevision((value) => value + 1)}>Refresh preview</Button>
          <Button variant="outlined" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button color="danger" loading={busy} disabled={!preview || !!preview.blockers.length} onClick={proceed}>
            {preview?.changed_count || preview?.related_records.length ? 'Proceed with reversal and listed changes' : 'Rewind import'}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
